// Prohlížeč souborů: obrázky (zoom myší i štípnutím, posun), jednoduché úpravy obrázků (otočit, překlopit, oříznout,
// zmenšit – náhled na plátně, výsledek počítá server), PDF, text s úpravou, video/audio.
import { registerComponent } from '@core/views/factory';
import { core, action, fetchBlob, ApiError } from '@core/api';
import { h, clamp } from '@core/dom';
import { icon } from '@core/icons';
import { t, tk } from '@core/i18n';
import { IwRibbon, type RibbonButton } from '@core/components/ribbon';
import { IwCommandBar, type BarItem } from '@core/components/commandbar';
import { useBar, renderBarHeader } from '@core/components/chrome';
import { emptyState, errorState, statusBar, loader } from '@core/components/misc';
import { toast } from '@core/components/toast';
import { prompt, confirmDiscard } from '@core/components/dialog';
import { events } from '@core/events';
import type { StorageItem } from '@core/contract';
import { createPdfView, type PdfView } from './pdf';

/**
 * Stažené obrázky sdílené všemi okny Prohlížeče (klíč id|verze|šířka, 0 = originál): předběžné načtení sousedů
 * při listování a návrat k předchozímu obrázku bez nového stahování. Nejvýš CACHE_MAX položek.
 */
const CACHE_MAX = 8;
const blobCache = new Map<string, Promise<Blob>>();
function cachedBlob(key: string, load: () => Promise<Blob>): Promise<Blob> {
  const hit = blobCache.get(key);
  if (hit) {
    blobCache.delete(key); // obnovit pořadí (naposledy použité na konec)
    blobCache.set(key, hit);
    return hit;
  }
  const p = load();
  p.catch(() => blobCache.delete(key));
  blobCache.set(key, p);
  while (blobCache.size > CACHE_MAX) blobCache.delete(blobCache.keys().next().value as string);
  return p;
}
/** Obrázky, u kterých se vyplatí nejdřív zmenšený náhled (velké, ne GIF – animace by se ztratila). */
const PREVIEW_MIN_BYTES = 400_000;
const previewable = (it: StorageItem): boolean => ['image/jpeg', 'image/png', 'image/webp'].includes(it.mime ?? '') && it.size > PREVIEW_MIN_BYTES;
/** Šířka náhledu podle okna a hustoty displeje: 1024 nebo 2048 (server je ukládá, další otevření je okamžité). */
const previewWidth = (box: HTMLElement): number => (Math.max(box.clientWidth, box.clientHeight, 600) * (window.devicePixelRatio || 1) <= 1024 ? 1024 : 2048);
const touchDevice = (): boolean => window.matchMedia('(pointer: coarse)').matches;

type Op = { op: 'rotate'; deg: 90 | -90 | 180 } | { op: 'flip'; dir: 'h' | 'v' } | { op: 'crop'; x: number; y: number; w: number; h: number } | { op: 'resize'; width: number };

registerComponent('prohlizec-view', ({ desc, win, wm }) => {
  const p = desc.payload as { item: StorageItem; text?: string; version?: number; editable?: boolean };
  const prefix = (JSON.parse(document.getElementById('iwan-boot')?.textContent ?? '{}') as { prefix?: string }).prefix ?? '';
  let item = p.item;
  let url = item.url ? prefix + item.url : '';
  const mime = item.mime ?? '';
  const isImage = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(mime);
  // nový vzhled: příkazy v liště záhlaví (hlavní akce Upravit), šipky na další / předchozí soubor po stranách;
  // klasické ovládání (Přizpůsobit) dál pás karet
  const bar = useBar() ? new IwCommandBar() : null;
  const classic = bar ? null : new IwRibbon();
  const navPrev = h('button', { type: 'button', class: 'viewer-nav prev', title: t('prohlizec.prev_file'), 'aria-label': t('prohlizec.prev_file') }, icon('chevron-left')) as HTMLButtonElement;
  const navNext = h('button', { type: 'button', class: 'viewer-nav next', title: t('prohlizec.next_file'), 'aria-label': t('prohlizec.next_file') }, icon('chevron')) as HTMLButtonElement;
  const toBar = (list: RibbonButton[]): BarItem[] => list.filter((b) => b.id !== 'prev' && b.id !== 'next').map((b) => ({
    id: b.id, label: b.label, icon: b.icon, shortcut: b.shortcut, disabled: b.disabled, danger: b.danger, menu: b.menu, onClick: b.onClick,
    place: b.primary ? 'primary' : b.id === 'open_tab' || b.id === 'save_copy' ? 'more' : 'action',
  }));
  const ribbon = {
    setButtons(list: RibbonButton[]): void {
      if (classic) { classic.setButtons(list); return; }
      bar?.setItems(toBar(list));
      if (bar) renderBarHeader({ win, wm, bar });
      const prev = list.find((b) => b.id === 'prev');
      const next = list.find((b) => b.id === 'next');
      navPrev.hidden = !prev; navNext.hidden = !next;
      navPrev.disabled = !!prev?.disabled; navNext.disabled = !!next?.disabled;
      navPrev.onclick = () => prev?.onClick?.(); navNext.onclick = () => next?.onClick?.();
    },
  };
  const body = h('div', { class: 'viewer-body' });
  const status = h('div');
  const el = h('div', { class: 'view viewer-view' + (bar ? ' chrome-bar' : '') }, classic ?? '', h('div', { class: 'viewer-stage' }, body, navPrev, navNext), status);
  navPrev.hidden = navNext.hidden = true;
  win.setTitle(`${t('prohlizec.name')} – ${item.name}`, 'image');
  let dirty = false;
  let version = p.version ?? 1;
  let textarea: HTMLTextAreaElement | null = null;
  let editing = false; // text
  let imgEdit = false; // obrázek
  const ops: Op[] = [];
  let cropping = false;
  let cropRatio: number | null = null;
  let pdf: PdfView | null = null;
  // listování: pořadí souborů od volajícího (Soubory: zobrazitelné soubory složky; pošta: přílohy zprávy)
  let nav: { ids: string[] } | null = null;
  const navIndex = (): number => (nav ? nav.ids.indexOf(item.id) : -1);
  const hasPrevFile = (): boolean => navIndex() > 0;
  const hasNextFile = (): boolean => nav !== null && navIndex() >= 0 && navIndex() < nav.ids.length - 1;
  async function goFile(delta: 1 | -1): Promise<void> {
    if (!nav) return;
    const id = nav.ids[navIndex() + delta];
    if (!id) return;
    if (dirty && !(await confirmDiscard())) return;
    dirty = false;
    await wm.reopen(win, { id }, { nav });
  }
  function applyTitle(): void {
    const pos = nav && navIndex() >= 0 ? ` (${navIndex() + 1} / ${nav.ids.length})` : '';
    win.setTitle(`${t('prohlizec.name')} – ${item.name}${pos}`, 'image');
  }

  // ---- zobrazení obrázku: plátno (originál + náhled úprav), zoom a posun
  const source = new Image();
  let hasOriginal = false; // na plátně je originál (ne zmenšený náhled)
  let originalLoad: Promise<void> | null = null;
  let origStatus = ''; // průběh dotahování originálu ve stavovém řádku
  let objectUrl: string | null = null;
  const canvas = h('canvas', { class: 'viewer-canvas' });
  const stage = h('div', { class: 'viewer-stage' }, canvas);
  const wrap = h('div', { class: 'viewer-img-wrap' }, stage);
  let zoom = 1;
  let tx = 0;
  let ty = 0;
  let fitScale = 1;

  function applyTransform(): void {
    stage.style.transform = `translate(${tx}px, ${ty}px) scale(${zoom})`;
    status.replaceChildren(statusBar(item.name, mime, hasOriginal ? `${canvas.width}×${canvas.height}` : t('prohlizec.preview_badge'), Math.round(zoom * fitScale * 100) + ' %', ...(origStatus ? [origStatus] : [])));
  }

  function setZoom(z: number, cx?: number, cy?: number): void {
    const nz = clamp(z, 0.1, 10);
    if (cx !== undefined && cy !== undefined) {
      // zoom do bodu: bod pod kurzorem zůstane na místě
      const r = wrap.getBoundingClientRect();
      const px = cx - r.left - tx;
      const py = cy - r.top - ty;
      tx -= (px / zoom) * (nz - zoom);
      ty -= (py / zoom) * (nz - zoom);
    }
    zoom = nz;
    applyTransform();
    if (!hasOriginal && zoom > 1.05) void ensureOriginal(); // přiblížení nad náhled: dotáhnout ostrý originál
  }

  function fit(keepView = false): void {
    if (!keepView) {
      zoom = 1;
      tx = 0;
      ty = 0;
    }
    const avail = { w: body.clientWidth - 24, h: body.clientHeight - 24 };
    fitScale = Math.min(1, avail.w / Math.max(1, canvas.width), avail.h / Math.max(1, canvas.height));
    canvas.style.width = Math.round(canvas.width * fitScale) + 'px';
    canvas.style.height = Math.round(canvas.height * fitScale) + 'px';
    applyTransform();
  }

  /** Náhled: aplikuje operace na originál do plátna (stejné pořadí jako server). keepView = výměna náhledu za originál beze skoku. */
  function renderPreview(keepView = false): void {
    let w = source.naturalWidth;
    let hh = source.naturalHeight;
    let cur = document.createElement('canvas');
    cur.width = w;
    cur.height = hh;
    cur.getContext('2d')?.drawImage(source, 0, 0);
    for (const op of ops) {
      const next = document.createElement('canvas');
      const ctx = next.getContext('2d');
      if (!ctx) break;
      if (op.op === 'rotate') {
        const quarter = op.deg === 180 ? 2 : 1;
        next.width = quarter === 2 ? w : hh;
        next.height = quarter === 2 ? hh : w;
        ctx.translate(next.width / 2, next.height / 2);
        ctx.rotate((op.deg * Math.PI) / 180);
        ctx.drawImage(cur, -w / 2, -hh / 2);
      } else if (op.op === 'flip') {
        next.width = w;
        next.height = hh;
        ctx.translate(op.dir === 'h' ? w : 0, op.dir === 'v' ? hh : 0);
        ctx.scale(op.dir === 'h' ? -1 : 1, op.dir === 'v' ? -1 : 1);
        ctx.drawImage(cur, 0, 0);
      } else if (op.op === 'crop') {
        next.width = op.w;
        next.height = op.h;
        ctx.drawImage(cur, op.x, op.y, op.w, op.h, 0, 0, op.w, op.h);
      } else if (op.op === 'resize') {
        if (op.width >= w) continue;
        next.width = op.width;
        next.height = Math.round((hh * op.width) / w);
        ctx.drawImage(cur, 0, 0, next.width, next.height);
      }
      cur = next;
      w = cur.width;
      hh = cur.height;
    }
    canvas.width = w;
    canvas.height = hh;
    canvas.getContext('2d')?.drawImage(cur, 0, 0);
    fit(keepView);
  }

  /** Obrázek z blobu do zdroje plátna (počká na dekódování). */
  function setSource(blob: Blob, original: boolean, keepView: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      const u = URL.createObjectURL(blob);
      source.onload = () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        objectUrl = u;
        hasOriginal = original;
        renderPreview(keepView);
        resolve();
      };
      source.onerror = () => { URL.revokeObjectURL(u); reject(new Error(t('prohlizec.load_error'))); };
      source.src = u;
    });
  }

  /** Originál (úpravy, přiblížení, počítač na pozadí); průběh ve stavovém řádku, blokující = s překryvem. */
  function ensureOriginal(blocking = false): Promise<void> {
    if (hasOriginal) return Promise.resolve();
    const ld = blocking ? loader(t('prohlizec.loading_original'), 0) : null;
    if (ld) body.appendChild(ld.el);
    originalLoad ??= cachedBlob(`${item.id}|${version}|0`, () => fetchBlob(url, (l, total) => {
      ld?.progress(l, total);
      origStatus = total ? t('prohlizec.original_progress', { p: Math.round((l / total) * 100) }) : '';
      applyTransform();
    }))
      .then((b) => setSource(b, true, true))
      .finally(() => { origStatus = ''; originalLoad = null; if (hasOriginal) applyTransform(); });
    const run = originalLoad;
    return run.finally(() => ld?.remove());
  }

  /** Otevření obrázku: velký → nejdřív náhled velikosti okna (rychle), originál na počítači hned na pozadí, na mobilu až když je třeba. */
  async function showImage(): Promise<void> {
    const ld = loader(t('prohlizec.loading_image'));
    body.appendChild(ld.el);
    try {
      if (previewable(item)) {
        const w = previewWidth(body);
        const blob = await cachedBlob(`${item.id}|${version}|${w}`, () => fetchBlob(`${url}&w=${w}`, (l, total) => ld.progress(l, total)));
        await setSource(blob, false, false);
        ld.remove();
        prefetchNeighbors();
        if (!touchDevice()) void ensureOriginal().catch(() => undefined);
      } else {
        const blob = await cachedBlob(`${item.id}|${version}|0`, () => fetchBlob(url, (l, total) => ld.progress(l, total)));
        await setSource(blob, true, false);
        ld.remove();
        prefetchNeighbors();
      }
    } catch (e) {
      ld.remove();
      body.replaceChildren(errorState(t('prohlizec.load_error') + ((e as ApiError).status ? ` (HTTP ${(e as ApiError).status})` : ''), () => render()));
    }
  }

  /** Při listování: předchozí a další obrázek se připraví na pozadí (popis okna + náhled do sdílené mezipaměti). */
  function prefetchNeighbors(): void {
    if (!nav) return;
    const i = navIndex();
    const ids = [nav.ids[i + 1], nav.ids[i - 1]].filter((x): x is string => typeof x === 'string');
    const w = previewWidth(body);
    void (async () => {
      for (const id of ids) {
        try {
          const d = await action<{ payload: { item: StorageItem; version?: number } }>(desc.app, desc.window, 'describe', { id });
          const it = d.payload.item;
          if (!it.url || !['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(it.mime ?? '')) continue;
          const v = d.payload.version ?? 1;
          const full = prefix + it.url;
          if (previewable(it)) await cachedBlob(`${it.id}|${v}|${w}`, () => fetchBlob(`${full}&w=${w}`));
          else if (it.size < 8_000_000) await cachedBlob(`${it.id}|${v}|0`, () => fetchBlob(full));
        } catch {
          // předběžné načtení je jen zrychlení
        }
      }
    })();
  }

  // posun myší / prstem, štípnutí dvěma prsty
  const pointers = new Map<number, { x: number; y: number }>();
  let pinchStart: { dist: number; zoom: number; cx: number; cy: number } | null = null;
  let swipe: { x: number; y: number; t: number; tx: number; ty: number } | null = null;
  wrap.addEventListener('pointerdown', (e) => {
    if (cropping) return;
    try { wrap.setPointerCapture(e.pointerId); } catch { /* syntetická událost bez aktivního ukazatele */ }
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    swipe = pointers.size === 1 && e.pointerType === 'touch' && zoom === 1 && !imgEdit ? { x: e.clientX, y: e.clientY, t: Date.now(), tx, ty } : null;
    if (pointers.size === 2) {
      const [a, b] = Array.from(pointers.values());
      if (a && b) pinchStart = { dist: Math.hypot(a.x - b.x, a.y - b.y), zoom, cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
    }
    wrap.classList.add('dragging');
  });
  wrap.addEventListener('pointermove', (e) => {
    const prev = pointers.get(e.pointerId);
    if (!prev) return;
    if (pointers.size === 2 && pinchStart) {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const [a, b] = Array.from(pointers.values());
      if (a && b) setZoom(pinchStart.zoom * (Math.hypot(a.x - b.x, a.y - b.y) / pinchStart.dist), pinchStart.cx, pinchStart.cy);
      return;
    }
    tx += e.clientX - prev.x;
    ty += e.clientY - prev.y;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    applyTransform();
  });
  const endPointer = (e: PointerEvent) => {
    if (swipe && pointers.size === 1 && e.type === 'pointerup') { // přejetí doleva = další, doprava = předchozí
      const dx = e.clientX - swipe.x;
      const dy = e.clientY - swipe.y;
      if (Math.abs(dx) > 70 && Math.abs(dy) < 60 && Date.now() - swipe.t < 700 && nav) { tx = swipe.tx; ty = swipe.ty; applyTransform(); void goFile(dx < 0 ? 1 : -1); }
    }
    swipe = null;
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinchStart = null;
    if (!pointers.size) wrap.classList.remove('dragging');
  };
  wrap.addEventListener('pointerup', endPointer);
  wrap.addEventListener('pointercancel', endPointer);
  wrap.addEventListener('wheel', (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      setZoom(zoom * (e.deltaY < 0 ? 1.1 : 0.9), e.clientX, e.clientY);
    }
  }, { passive: false });
  wrap.addEventListener('dblclick', () => (zoom === 1 && tx === 0 && ty === 0 ? setZoom(2) : fit()));

  // ---- oříznutí: rámeček nad plátnem v souřadnicích zobrazení, výsledek v pixelech obrázku
  let frame: HTMLElement | null = null;
  let rect = { x: 0, y: 0, w: 0, h: 0 }; // v px zobrazení (po fitScale), relativně k plátnu

  function startCrop(): void {
    cropping = true;
    fit();
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    rect = { x: Math.round(cw * 0.1), y: Math.round(ch * 0.1), w: Math.round(cw * 0.8), h: Math.round(ch * 0.8) };
    if (cropRatio) applyRatio();
    frame = h('div', { class: 'crop-frame' }, ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map((d) => h('div', { class: 'crop-handle ' + d, dataset: { d } })));
    stage.appendChild(frame);
    paintFrame();
    frame.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      const handle = (e.target as HTMLElement).dataset['d'] ?? 'move';
      const start = { x: e.clientX, y: e.clientY, r: { ...rect } };
      frame?.setPointerCapture(e.pointerId);
      const move = (ev: PointerEvent) => {
        const dx = (ev.clientX - start.x) / zoom;
        const dy = (ev.clientY - start.y) / zoom;
        const r = { ...start.r };
        const cw2 = canvas.clientWidth;
        const ch2 = canvas.clientHeight;
        if (handle === 'move') {
          r.x = clamp(r.x + dx, 0, cw2 - r.w);
          r.y = clamp(r.y + dy, 0, ch2 - r.h);
        } else {
          if (handle.includes('e')) r.w = clamp(r.w + dx, 16, cw2 - r.x);
          if (handle.includes('s')) r.h = clamp(r.h + dy, 16, ch2 - r.y);
          if (handle.includes('w')) {
            const nx = clamp(r.x + dx, 0, r.x + r.w - 16);
            r.w += r.x - nx;
            r.x = nx;
          }
          if (handle.includes('n')) {
            const ny = clamp(r.y + dy, 0, r.y + r.h - 16);
            r.h += r.y - ny;
            r.y = ny;
          }
          if (cropRatio) {
            if (handle === 'n' || handle === 's') r.w = Math.min(cw2 - r.x, r.h * cropRatio);
            else r.h = Math.min(ch2 - r.y, r.w / cropRatio);
          }
        }
        rect = r;
        paintFrame();
      };
      const up = () => {
        frame?.removeEventListener('pointermove', move);
        frame?.removeEventListener('pointerup', up);
      };
      frame?.addEventListener('pointermove', move);
      frame?.addEventListener('pointerup', up);
    });
    ribbon.setButtons(buttons());
  }

  function applyRatio(): void {
    if (!cropRatio) return;
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    let w = rect.w;
    let hh = w / cropRatio;
    if (hh > ch) {
      hh = ch;
      w = hh * cropRatio;
    }
    rect = { x: clamp(rect.x, 0, cw - w), y: clamp(rect.y, 0, ch - hh), w, h: hh };
  }

  function paintFrame(): void {
    if (!frame) return;
    Object.assign(frame.style, { left: rect.x + 'px', top: rect.y + 'px', width: rect.w + 'px', height: rect.h + 'px' });
  }

  function endCrop(apply: boolean): void {
    if (apply && frame) {
      const sx = canvas.width / canvas.clientWidth;
      const sy = canvas.height / canvas.clientHeight;
      pushOp({ op: 'crop', x: Math.round(rect.x * sx), y: Math.round(rect.y * sy), w: Math.max(1, Math.round(rect.w * sx)), h: Math.max(1, Math.round(rect.h * sy)) });
    }
    frame?.remove();
    frame = null;
    cropping = false;
    ribbon.setButtons(buttons());
  }

  function pushOp(op: Op): void {
    ops.push(op);
    dirty = true;
    win.classList.add('dirty');
    renderPreview();
    ribbon.setButtons(buttons());
  }

  function setImgEdit(on: boolean): void {
    imgEdit = on;
    el.classList.toggle('editing', on);
    if (!on) {
      if (cropping) endCrop(false);
      ops.length = 0;
      dirty = false;
      win.classList.remove('dirty');
      renderPreview();
    }
    ribbon.setButtons(buttons());
  }

  async function saveImage(asCopy: boolean): Promise<void> {
    if (!ops.length) return;
    try {
      const r = await core<{ item: StorageItem & { version?: number } }>('storage', 'imageEdit', { id: item.id, ops, mode: asCopy ? 'copy' : 'overwrite', version });
      events.kick();
      if (asCopy) {
        toast(t('prohlizec.saved_copy'), 'ok');
        return;
      }
      item = r.item;
      version = r.item.version ?? version + 1;
      url = item.url ? prefix + item.url : url;
      ops.length = 0;
      dirty = false;
      win.classList.remove('dirty');
      toast(t('prohlizec.saved'), 'ok');
      loadSource(url + '&v=' + version);
      setImgEdit(false);
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  function loadSource(src: string): void {
    source.onload = () => { hasOriginal = true; renderPreview(); };
    source.src = src;
  }

  // ---- ribbon
  const buttons = (): RibbonButton[] => {
    const b: RibbonButton[] = [];
    if (isImage && imgEdit) {
      if (cropping) {
        b.push({ id: 'crop_apply', label: t('prohlizec.crop_apply'), icon: 'check', group: 'prohlizec.grp_crop', primary: true, onClick: () => endCrop(true) });
        b.push({ id: 'crop_cancel', label: t('prohlizec.crop_cancel'), icon: 'x', group: 'prohlizec.grp_crop', onClick: () => endCrop(false) });
        const ratios: { label: string; v: number | null }[] = [{ label: t('prohlizec.ratio_free'), v: null }, { label: '1:1', v: 1 }, { label: '4:3', v: 4 / 3 }, { label: '3:2', v: 3 / 2 }, { label: '16:9', v: 16 / 9 }];
        b.push({ id: 'ratio', label: t('prohlizec.ratio'), icon: 'crop', group: 'prohlizec.grp_crop', menu: ratios.map((r) => ({ label: r.label, checked: cropRatio === r.v, onSelect: () => { cropRatio = r.v; applyRatio(); paintFrame(); } })) });
        return b;
      }
      b.push({ id: 'rot_l', label: t('prohlizec.rotate_left'), icon: 'rotate-left', group: 'prohlizec.grp_transform', onClick: () => pushOp({ op: 'rotate', deg: -90 }) });
      b.push({ id: 'rot_r', label: t('prohlizec.rotate_right'), icon: 'rotate-right', group: 'prohlizec.grp_transform', onClick: () => pushOp({ op: 'rotate', deg: 90 }) });
      b.push({ id: 'flip_h', label: t('prohlizec.flip_h'), icon: 'flip-h', group: 'prohlizec.grp_transform', onClick: () => pushOp({ op: 'flip', dir: 'h' }) });
      b.push({ id: 'flip_v', label: t('prohlizec.flip_v'), icon: 'flip-v', group: 'prohlizec.grp_transform', onClick: () => pushOp({ op: 'flip', dir: 'v' }) });
      b.push({ id: 'crop', label: t('prohlizec.crop'), icon: 'crop', group: 'prohlizec.grp_crop', onClick: () => startCrop() });
      b.push({
        id: 'resize', label: t('prohlizec.resize'), icon: 'resize', group: 'prohlizec.grp_crop',
        menu: [
          ...[800, 1280, 1920].map((w) => ({ label: `${w} px`, disabled: canvas.width <= w, onSelect: () => pushOp({ op: 'resize', width: w }) })),
          { label: t('prohlizec.resize_custom'), onSelect: () => void prompt(t('prohlizec.resize_prompt'), String(canvas.width)).then((v) => { const n = Number(v); if (v && n >= 16 && n < canvas.width) pushOp({ op: 'resize', width: Math.round(n) }); }) },
        ],
      });
      b.push({ id: 'undo', label: t('prohlizec.undo'), icon: 'undo', shortcut: 'Ctrl+Z', group: 'prohlizec.grp_crop', disabled: !ops.length, onClick: () => { ops.pop(); dirty = ops.length > 0; win.classList.toggle('dirty', dirty); renderPreview(); ribbon.setButtons(buttons()); } });
      b.push({ id: 'save', label: t('prohlizec.save'), icon: 'save', shortcut: 'Ctrl+S', group: 'prohlizec.grp_save', primary: true, disabled: !ops.length, onClick: () => void saveImage(false) });
      b.push({ id: 'save_copy', label: t('prohlizec.save_copy'), icon: 'save-copy', group: 'prohlizec.grp_save', disabled: !ops.length, onClick: () => void saveImage(true) });
      b.push({ id: 'cancel_edit', label: t('prohlizec.cancel_edit'), icon: 'x', shortcut: 'Esc', group: 'prohlizec.grp_save', onClick: () => void cancelImgEdit() });
      return b;
    }
    if (nav) {
      b.push({ id: 'prev', label: t('prohlizec.prev_file'), icon: 'chevron-left', shortcut: '←', group: 'prohlizec.grp_nav', order: -2, disabled: !hasPrevFile(), onClick: () => void goFile(-1) });
      b.push({ id: 'next', label: t('prohlizec.next_file'), icon: 'chevron', shortcut: '→', group: 'prohlizec.grp_nav', order: -2, disabled: !hasNextFile(), onClick: () => void goFile(1) });
    }
    b.push({ id: 'download', label: t('prohlizec.download'), icon: 'download', group: 'core.grp.actions', onClick: () => url && window.open(url + '&dl=1', '_blank') });
    if (pdf) {
      const v = pdf;
      b.push({ id: 'zin', label: t('prohlizec.zoom_in'), icon: 'zoom-in', group: 'core.grp.view', onClick: () => v.zoomIn() });
      b.push({ id: 'zout', label: t('prohlizec.zoom_out'), icon: 'zoom-out', group: 'core.grp.view', onClick: () => v.zoomOut() });
      b.push({ id: 'fit', label: t('prohlizec.zoom_fit'), icon: 'fit', group: 'core.grp.view', onClick: () => v.fit() });
      b.push({ id: 'find', label: t('prohlizec.pdf_find'), icon: 'search', shortcut: 'Ctrl+F', group: 'prohlizec.grp_find', onClick: () => void prompt(t('prohlizec.pdf_find_prompt'), '', { title: t('prohlizec.pdf_find') }).then((q) => { if (q !== null) void v.find(q); }) });
      b.push({ id: 'find_next', label: t('prohlizec.pdf_find_next'), icon: 'chevron', shortcut: 'F3', group: 'prohlizec.grp_find', disabled: !v.hasHits(), onClick: () => v.findNext(1) });
      b.push({ id: 'open_tab', label: t('prohlizec.pdf_open_tab'), icon: 'window', group: 'core.grp.actions', onClick: () => url && window.open(url, '_blank') });
    }
    if (isImage) {
      b.push({ id: 'zin', label: t('prohlizec.zoom_in'), icon: 'zoom-in', group: 'core.grp.view', onClick: () => setZoom(zoom * 1.25) });
      b.push({ id: 'zout', label: t('prohlizec.zoom_out'), icon: 'zoom-out', group: 'core.grp.view', onClick: () => setZoom(zoom / 1.25) });
      b.push({ id: 'fit', label: t('prohlizec.zoom_fit'), icon: 'fit', group: 'core.grp.view', onClick: () => fit() });
      b.push({ id: 'edit_image', label: t('prohlizec.edit_image'), icon: 'edit', shortcut: 'F2', group: 'core.grp.edit', primary: true, onClick: () => void ensureOriginal(true).then(() => setImgEdit(true)).catch((e: Error) => toast(e.message, 'error')) });
    }
    if (p.editable) {
      if (!editing) b.push({ id: 'edit', label: t('prohlizec.edit'), icon: 'edit', shortcut: 'F2', group: 'core.grp.edit', primary: true, onClick: () => setEditing(true) });
      else b.push({ id: 'save', label: t('prohlizec.save'), icon: 'save', shortcut: 'Ctrl+S', group: 'core.grp.edit', primary: true, onClick: () => void save() });
    }
    return b;
  };

  async function cancelImgEdit(): Promise<void> {
    if (ops.length && !(await confirmDiscard(t('prohlizec.discard_edits')))) return;
    setImgEdit(false);
  }

  function render(): void {
    body.replaceChildren();
    if (isImage) {
      body.appendChild(wrap);
      void showImage();
    } else if (mime.startsWith('image/')) {
      const ld = loader(t('prohlizec.loading_image'));
      const img = h('img', { src: url, alt: item.name, class: 'viewer-img' }) as HTMLImageElement;
      img.addEventListener('load', () => ld.remove());
      img.addEventListener('error', () => { ld.remove(); body.replaceChildren(errorState(t('prohlizec.load_error'), () => render())); });
      body.append(h('div', { class: 'viewer-img-wrap' }, img), ld.el);
      status.replaceChildren(statusBar(item.name, mime));
    } else if (mime === 'application/pdf') {
      // vlastní čtečka (stejná na počítači i na mobilu; Chrome na Androidu PDF v rámu neumí)
      pdf = createPdfView({
        url, assetsBase: prefix + '/assets/pdfjs/',
        onStatus: (page, pages, percent, extra) => { status.replaceChildren(statusBar(item.name, 'PDF', t('prohlizec.pdf_page', { page, pages }) + (extra ? ' · ' + extra : ''), percent + ' %')); ribbon.setButtons(buttons()); },
        onError: (m) => body.replaceChildren(errorState(t('prohlizec.pdf_error', { error: m }), () => { pdf?.destroy(); pdf = null; render(); })),
      });
      body.appendChild(pdf.el);
      status.replaceChildren(statusBar(item.name, 'PDF', t('prohlizec.pdf_loading')));
    } else if (typeof p.text === 'string') {
      textarea = h('textarea', { class: 'viewer-text', spellcheck: 'false', readonly: !editing }, p.text);
      textarea.addEventListener('input', () => {
        dirty = true;
        win.classList.add('dirty');
      });
      body.appendChild(textarea);
      status.replaceChildren(statusBar(item.name, mime, p.text.length + ' zn.'));
    } else if (mime.startsWith('video/')) {
      const ld = loader(t('prohlizec.loading_video'));
      const video = h('video', { class: 'viewer-media', src: url, controls: true, preload: 'metadata' }) as HTMLVideoElement;
      video.addEventListener('loadeddata', () => ld.remove());
      video.addEventListener('error', () => { ld.remove(); body.replaceChildren(errorState(t('prohlizec.load_error'), () => render())); });
      body.append(video, ld.el);
    } else if (mime.startsWith('audio/')) {
      body.appendChild(h('audio', { class: 'viewer-media', src: url, controls: true }));
    } else {
      body.appendChild(emptyState(t('prohlizec.unsupported'), 'file'));
    }
    ribbon.setButtons(buttons());
  }

  function setEditing(on: boolean): void {
    editing = on;
    if (textarea) {
      textarea.readOnly = !on;
      if (on) textarea.focus();
    }
    ribbon.setButtons(buttons());
  }

  async function save(): Promise<void> {
    if (!textarea) return;
    try {
      const r = await core<{ item: StorageItem }>('storage', 'write', { id: item.id, content: textarea.value, version });
      version = (r.item as unknown as { version?: number }).version ?? version + 1;
      dirty = false;
      win.classList.remove('dirty');
      toast(t('prohlizec.saved'), 'ok');
      setEditing(false);
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  const ro = new ResizeObserver(() => {
    if (isImage && zoom === 1 && source.naturalWidth) fit();
  });
  ro.observe(body);
  render();

  return {
    el,
    setMode: () => ribbon.setButtons(buttons()), // PC ↔ telefon: jiné záhlaví
    canClose: async () => (dirty ? confirmDiscard() : true),
    getState: () => ({ zoom, editing, nav }),
    setState: (s) => {
      const st = s as { zoom?: number; editing?: boolean; nav?: { ids?: unknown } } | null;
      if (st?.nav && Array.isArray(st.nav.ids)) { nav = { ids: st.nav.ids.filter((x): x is string => typeof x === 'string') }; applyTitle(); ribbon.setButtons(buttons()); }
      if (st?.zoom) setZoom(st.zoom);
      if (st?.editing) setEditing(true);
    },
    destroy: () => { ro.disconnect(); pdf?.destroy(); pdf = null; },
    shortcut: (sc: string) => {
      if (sc === 'Ctrl+S' && editing) {
        void save();
        return true;
      }
      if (sc === 'Ctrl+S' && imgEdit && ops.length) {
        void saveImage(false);
        return true;
      }
      if (sc === 'Ctrl+Z' && imgEdit && ops.length) {
        ops.pop();
        dirty = ops.length > 0;
        win.classList.toggle('dirty', dirty);
        renderPreview();
        ribbon.setButtons(buttons());
        return true;
      }
      if (sc === 'Esc' && imgEdit) {
        if (cropping) endCrop(false);
        else void cancelImgEdit();
        return true;
      }
      if (sc === 'F2' && isImage && !imgEdit) {
        setImgEdit(true);
        return true;
      }
      if (sc === 'F2' && p.editable && !editing) {
        setEditing(true);
        return true;
      }
      if (sc === 'Ctrl+F' && pdf) {
        const v = pdf;
        void prompt(t('prohlizec.pdf_find_prompt'), '', { title: t('prohlizec.pdf_find') }).then((q) => { if (q !== null) void v.find(q); });
        return true;
      }
      if (sc === 'F3' && pdf) {
        pdf.findNext(1);
        return true;
      }
      if ((sc === 'ArrowRight' || sc === 'PageDown') && nav && !editing) { void goFile(1); return true; }
      if ((sc === 'ArrowLeft' || sc === 'PageUp') && nav && !editing) { void goFile(-1); return true; }
      return false;
    },
  } as ReturnType<Parameters<typeof registerComponent>[1]> & { shortcut(sc: string): boolean };
});
